import { createSelector } from 'reselect';

import { createOptionalParameterSelector, createParameterSelector, OperationStatus } from '@pressreader/appstore';
import { type IArticle, type IIssueInfo, type IIssueInfoPageArticle, isIssueInfoArticleRef } from '@pressreader/content-types';
import { buildCidDateKey, extractCidDateKeyString, resolveArticleGroupId } from '@pressreader/content-utils';

import type { IssueInfoPageArticleComparator, ZeroColumnArticleGrouper, ZeroColumnArticlesGroup } from '../../types';
import { buildCidDateLanguageKey, isLanguageKeyOfCidDate } from '../../utils';
import { getIssueState } from '../reducers';
import type { State as IssueInfoReducerState } from '../reducers/issueinfo';

const getState = createSelector(getIssueState, state => state.issueInfo);

type IssueInfoEntry = IssueInfoReducerState['info'][string];
type IssueCalendarInfoEntry = IssueInfoReducerState['calendar'][string];
type IssueInfoMap = IssueInfoReducerState['info'];
type IssueCalendarInfoMap = IssueInfoReducerState['calendar'];

const selectIssueInfoMap = createSelector(getState, state => state.info as IssueInfoMap);

const selectInfoGeneration = createSelector(getState, state => state.infoGeneration);

const selectCidDateKey = createParameterSelector<'cidDateKey', string>('cidDateKey');

const selectIssueInfoByCidDateKey = createSelector(
    selectCidDateKey,
    selectIssueInfoMap,
    (cidDateKey, infoMap): IssueInfoEntry | null => infoMap[cidDateKey] ?? null,
);

const selectIssueLangByCidDateKey = createSelector(
    selectIssueInfoByCidDateKey,
    (infoMap): IIssueInfo['newspaper']['isoLanguage'] | null => infoMap?.info?.newspaper?.isoLanguage ?? null,
);

const selectCid = createParameterSelector<'cid', string>('cid');
const selectIssueDate = createOptionalParameterSelector<'issueDate', Date>('issueDate');
const selectLanguageCode = createOptionalParameterSelector<'languageCode', string>('languageCode');

const selectIssueInfoByCidDateLanguage = createSelector(
    selectCid,
    selectIssueDate,
    selectLanguageCode,
    selectIssueInfoMap,
    (cid, issueDate, languageCode, map): IssueInfoEntry | undefined => map[buildCidDateLanguageKey({ cid, issueDate, languageCode })],
);

/**
 * Whether a load of the same issue is in flight under a key of the other shape: the language-free
 * key when a language was requested, any language key when none was (`isLanguageKeyOfCidDate`). A
 * response whose language is the newspaper's own answers both request shapes, so a requester that
 * sees such a load waits for it instead of fetching the whole issue a second time.
 */
const selectIsIssueInfoLoadingUnderSiblingLanguageKey = createSelector(
    selectCid,
    selectIssueDate,
    selectLanguageCode,
    selectIssueInfoMap,
    (cid, issueDate, languageCode, infoMap): boolean => {
        const cidDateKey = buildCidDateKey(cid, issueDate);
        const isLoading = (entry: IssueInfoEntry | undefined) =>
            entry?.loading === OperationStatus.Pending || entry?.loading === OperationStatus.Queued;

        if (languageCode) {
            return isLoading(infoMap[cidDateKey]);
        }

        return Object.keys(infoMap).some(key => isLanguageKeyOfCidDate(key, cidDateKey) && isLoading(infoMap[key]));
    },
);

const selectIssueInfoByCidDateKeyLanguage = createSelector(
    selectCidDateKey,
    selectLanguageCode,
    selectIssueInfoMap,
    (cidDateKey, languageCode, infoMap): IssueInfoEntry | null => infoMap[buildCidDateLanguageKey(cidDateKey, languageCode)] || null,
);

const selectIssueCalendarInfoMap = createSelector(getState, state => state.calendar as IssueCalendarInfoMap);

const selectIssueCalendarInfoByCid = createSelector(
    selectCid,
    selectIssueCalendarInfoMap,
    (cid, map): IssueCalendarInfoEntry | null => map[cid] || null,
);

const selectIssueInfoArticlesByIssueInfo = (issueInfo: IssueInfoEntry | null) =>
    (issueInfo?.loading === OperationStatus.Done &&
        issueInfo?.info?.pages?.reduce((acc, page) => acc.concat(page.articles), [] as IIssueInfoPageArticle[])) ||
    null;

const selectIssueInfoArticlesByCidDateKey = createSelector(selectIssueInfoByCidDateKey, selectIssueInfoArticlesByIssueInfo);

const selectIssueKeyParam = createParameterSelector<'issueKey', string>('issueKey');
const selectCidDateKeyByIssueKey = createSelector(selectIssueKeyParam, issueKey => extractCidDateKeyString(issueKey));
const selectIssueInfoByIssueKey = createSelector(
    selectCidDateKeyByIssueKey,
    selectIssueInfoMap,
    (cidDateKey, infoMap): IssueInfoEntry | null => infoMap[cidDateKey] ?? null,
);

const selectArticleIdParam = createParameterSelector<'articleId', IArticle['id']>('articleId');
const selectIssueArticleByIssueKeyAndArticleId = createSelector(
    selectIssueInfoByIssueKey,
    selectArticleIdParam,
    (issueInfo, articleId) => selectIssueInfoArticlesByIssueInfo(issueInfo)?.find(article => article.id === articleId) ?? null,
);

/**
 * Comparator function to sort articles in the result of the selector.
 */
const selectArticleSorterParam = createParameterSelector<'comparator', IssueInfoPageArticleComparator>('comparator');
/**
 * @default undefined - no limit
 */
const selectArticleLimitParam = createParameterSelector<'limit', number | null>('limit');
const selectZeroColumnArticleGrouperParam = createParameterSelector<'groupArticle', ZeroColumnArticleGrouper>('groupArticle');

const selectIssueInfoSortedZeroColumnArticlesByCidDateKey = createSelector(
    selectIssueInfoArticlesByCidDateKey,
    selectIssueInfoByCidDateKey,
    selectArticleSorterParam,
    selectArticleLimitParam,
    selectZeroColumnArticleGrouperParam,
    (issueArticles, issueInfo, comparator, limit, groupArticle) => {
        if (issueInfo?.loading !== OperationStatus.Done || !issueInfo.info) {
            return null;
        }
        if (!issueArticles) {
            return null;
        }

        const contentRefPrimaryArticleIds = new Set(issueInfo.info.contentRefs.filter(isIssueInfoArticleRef).map(resolveArticleGroupId));
        const validIssueArticles = issueArticles.filter(article => contentRefPrimaryArticleIds.has(resolveArticleGroupId(article)));
        if (!validIssueArticles.length) {
            return [];
        }

        const groupedArticles = validIssueArticles.reduce(
            (groups, article) => groupArticle(groups, article, resolveArticleGroupId),
            new Map<string, ZeroColumnArticlesGroup>(),
        );

        const primaryDedupedRepresentatives = [...groupedArticles.values()]
            .map(group => ({
                representative: group.representative,
                articleForSorting:
                    group.representative.rate === group.maxRate ? group.representative : { ...group.representative, rate: group.maxRate },
            }))
            .sort((a, b) => comparator(a.articleForSorting, b.articleForSorting))
            .map(group => group.representative)
            .slice(0, limit ?? undefined);

        return primaryDedupedRepresentatives;
    },
);

const selectIssueArticleTitleByIssueKeyAndArticleId = createSelector(selectIssueArticleByIssueKeyAndArticleId, article => article?.title ?? null);

export {
    selectInfoGeneration,
    selectIsIssueInfoLoadingUnderSiblingLanguageKey,
    selectIssueArticleTitleByIssueKeyAndArticleId,
    selectIssueCalendarInfoByCid,
    selectIssueInfoArticlesByCidDateKey,
    selectIssueInfoByCidDateKey,
    selectIssueInfoByCidDateKeyLanguage,
    selectIssueInfoByCidDateLanguage,
    selectIssueInfoSortedZeroColumnArticlesByCidDateKey,
    selectIssueLangByCidDateKey,
};
